import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GitBranch,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Check,
  RefreshCw,
  Clock,
  Terminal,
  Code2,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  CheckCircle,
  FileCode2,
  Layers,
} from "lucide-react";
import { useAuthicatedUser, useAuthState } from "react-redux-django-auth";
import { authClientWeb } from "react-redux-django-auth/web";
import axios from "axios";
import Button from "../../../components/common/Button";
import { useToast } from "../../../hooks/otherHooks/useToast";
import {
  useStartAssignmentMutation,
  useExerciseSubmissionQuery,
  useCourseSubmissionQuery,
} from "../../../hooks/queries/useAssignmentQueries";
import LaunchCodespaceButton from "./LaunchCodespaceButton";
import CodespaceActionButton from "./CodespaceActionButton";

export { LaunchCodespaceButton, CodespaceActionButton };

/**
 * Matches an exercise's target file name / path (e.g. 'module_01/variables.py' or 'variables.py')
 * against the dictionary of file/module scores reported by the CI autograder.
 */
export function resolveExerciseFileScore(
  scoresMap: Record<string, any> | undefined | null,
  targetRef?: string | null
): { score: number | null; matchedKey: string | null } {
  if (!targetRef || !scoresMap || typeof scoresMap !== "object") {
    return { score: null, matchedKey: null };
  }

  const rawRef = String(targetRef).trim();
  if (!rawRef) {
    return { score: null, matchedKey: null };
  }

  // 1. Direct exact key match
  if (rawRef in scoresMap && scoresMap[rawRef] !== null && scoresMap[rawRef] !== undefined) {
    const val = Number(scoresMap[rawRef]);
    if (!Number.isNaN(val)) {
      return { score: val, matchedKey: rawRef };
    }
  }

  const normRef = rawRef.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "").toLowerCase();
  const refParts = normRef.split("/");
  const refBasename = refParts[refParts.length - 1];
  const refStem = refBasename.includes(".")
    ? refBasename.slice(0, refBasename.lastIndexOf("."))
    : refBasename;
  const refStemNoTest = refStem.startsWith("test_") ? refStem.slice(5) : refStem;

  const entries = Object.entries(scoresMap);

  // 2. Case-insensitive normalized full path match
  for (const [k, v] of entries) {
    const normK = String(k).trim().replace(/\\/g, "/").replace(/^\/+|\/+$/g, "").toLowerCase();
    if (normK === normRef) {
      const val = Number(v);
      if (!Number.isNaN(val)) return { score: val, matchedKey: k };
    }
  }

  // 3. Basename match (e.g. 'variables.py' matches 'module_01/variables.py')
  for (const [k, v] of entries) {
    const normK = String(k).trim().replace(/\\/g, "/").replace(/^\/+|\/+$/g, "").toLowerCase();
    const parts = normK.split("/");
    const kBasename = parts[parts.length - 1];
    if (kBasename === refBasename) {
      const val = Number(v);
      if (!Number.isNaN(val)) return { score: val, matchedKey: k };
    }
  }

  // 4. Stem / test_<stem> match (e.g. 'variables.py' matches 'test_variables.py' or 'variables')
  for (const [k, v] of entries) {
    const normK = String(k).trim().replace(/\\/g, "/").replace(/^\/+|\/+$/g, "").toLowerCase();
    const parts = normK.split("/");
    const kBasename = parts[parts.length - 1];
    const kStem = kBasename.includes(".")
      ? kBasename.slice(0, kBasename.lastIndexOf("."))
      : kBasename;
    const kStemNoTest = kStem.startsWith("test_") ? kStem.slice(5) : kStem;

    if (kStem === refStem || (refStemNoTest && kStemNoTest === refStemNoTest)) {
      const val = Number(v);
      if (!Number.isNaN(val)) return { score: val, matchedKey: k };
    }
  }

  return { score: null, matchedKey: null };
}

/**
 * Shared hook to resolve the student's current GitHub username (from Redux or /auth/users/me/ fallback)
 */
function useResolvedGithubUsername(submissionGithubUsername?: string) {
  const { currentUser, refreshUser } = useAuthicatedUser();
  const { user: authStateUser } = useAuthState();
  const [fetchedGithubUsername, setFetchedGithubUsername] = useState<string | null>(null);

  const resolvedUser =
    (currentUser as any)?.user ||
    (currentUser as any)?.data?.user ||
    currentUser ||
    (authStateUser as any)?.user ||
    (authStateUser as any)?.data?.user ||
    authStateUser;

  useEffect(() => {
    let isMounted = true;
    const fetchLatestUser = async () => {
      try {
        let client;
        try {
          client = authClientWeb();
        } catch {
          // fallback
        }
        if (!client) {
          const token = localStorage.getItem("access");
          const baseURL = process.env.REACT_APP_API_URL || "";
          client = axios.create({
            baseURL,
            headers: token ? { Authorization: `JWT ${token}` } : {},
          });
        }
        const res = await client.get("/auth/users/me/");
        if (isMounted && res.data?.github_username) {
          setFetchedGithubUsername(res.data.github_username);
        }
      } catch {
        // silent fallback
      }
    };

    if (!resolvedUser?.github_username) {
      if (refreshUser) {
        refreshUser();
      }
      fetchLatestUser();
    }
    return () => {
      isMounted = false;
    };
  }, [resolvedUser?.github_username, refreshUser]);

  return (
    resolvedUser?.github_username ||
    fetchedGithubUsername ||
    submissionGithubUsername ||
    ""
  );
}

/* ============================================================================
 * 1. COURSE-LEVEL GITHUB WORKSPACE BANNER (Rendered on Course Classwork Header)
 * ============================================================================ */
export interface CourseGitHubWorkspaceBannerProps {
  courseId?: number | string;
  courseTitle?: string;
  templateRepo?: string;
  initialSubmission?: any;
  track?: string;
  hasAutogradedExercises?: boolean;
}

export const CourseGitHubWorkspaceBanner: React.FC<CourseGitHubWorkspaceBannerProps> = ({
  courseId,
  courseTitle,
  templateRepo,
  initialSubmission,
  track,
  hasAutogradedExercises,
}) => {
  const { addToast } = useToast();
  const [copiedClone, setCopiedClone] = useState(false);

  const {
    data: liveCourseSubmission,
    isFetching: isRefetching,
    refetch,
  } = useCourseSubmissionQuery(courseId);

  const { mutateAsync: startAssignment, isPending: isStarting } = useStartAssignmentMutation();

  const submission = liveCourseSubmission || initialSubmission || null;
  const currentGithubUsername = useResolvedGithubUsername(submission?.github_username);

  // Only render if the course has a template repo, an existing submission, or autograded exercises
  if (!templateRepo && !submission && !hasAutogradedExercises) {
    return null;
  }

  const repoName = submission?.repository_name;
  const repoUrl = submission?.repository_url;
  const cloneCommand = repoUrl ? `git clone ${repoUrl}.git` : "";
  const gradedFilesCount =
    submission?.module_scores && typeof submission.module_scores === "object"
      ? Object.keys(submission.module_scores).length
      : 0;

  const handleCopyClone = () => {
    if (!cloneCommand) return;
    navigator.clipboard.writeText(cloneCommand);
    setCopiedClone(true);
    addToast("Course repository clone command copied!", "success");
    setTimeout(() => setCopiedClone(false), 2500);
  };

  const handleProvisionCourseRepo = async () => {
    if (!currentGithubUsername) {
      addToast("Please connect your GitHub account in Profile Settings first.", "warning");
      return;
    }

    try {
      const res = await startAssignment({
        ...(templateRepo ? { template_repo_name: templateRepo } : {}),
        github_username: currentGithubUsername,
        course_id: courseId,
        track: track || courseTitle,
      });

      addToast(
        res.is_new
          ? "Course GitHub repository provisioned! Clone it to complete all exercises in this course."
          : "Course GitHub repository linked and ready!",
        "success"
      );
      refetch();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Failed to provision course GitHub repository.";
      addToast(detail, "error");
    }
  };

  return (
    <div className="cht-bg-white cht-rounded-[24px] cht-border cht-border-indigo-100 cht-shadow-[0_4px_24px_rgba(62,87,218,0.05)] cht-p-5 sm:cht-p-6 cht-space-y-4">
      <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center lg:cht-justify-between cht-gap-4">
        <div className="cht-flex cht-items-start cht-gap-3.5">
          <div className="cht-w-11 cht-h-11 cht-rounded-2xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
            <GitBranch size={22} />
          </div>
          <div className="cht-space-y-1">
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
              <h3 className="cht-text-sm sm:cht-text-base cht-font-bold cht-text-slate-900 !cht-font-body">
                Course GitHub Workspace
              </h3>
              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-indigo-50 cht-text-[#3e57da] cht-border cht-border-[#3e57da]/20">
                <Sparkles size={10} /> Single Repo for All Course Exercises
              </span>
              {currentGithubUsername && (
                <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-font-semibold cht-text-slate-500">
                  • @{currentGithubUsername}
                </span>
              )}
            </div>
            <p className="cht-text-xs cht-text-slate-500 leading-relaxed !cht-font-body">
              {submission
                ? "Your private course repository is active. Complete each exercise in its designated file inside this repository and push to main."
                : "Provision your private course repository from the course template once. Every coding exercise in this course will be graded by its file name inside this repository."}
            </p>
          </div>
        </div>

        {/* Right Action Area */}
        <div className="cht-flex cht-items-center cht-gap-2.5 cht-shrink-0 cht-flex-wrap">
          {!currentGithubUsername ? (
            <Link
              to="/student/dashboard/profile?tab=edit-profile"
              className="cht-inline-flex cht-items-center cht-gap-2 cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-xs cht-font-bold cht-bg-[#1e293b] hover:cht-bg-slate-900 cht-text-white cht-transition-all cht-shadow-sm"
            >
              <span>Connect GitHub in Profile Settings</span>
              <ArrowRight size={13} />
            </Link>
          ) : !submission ? (
            <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
              <Button
                variant="primary"
                size="sm"
                className="cht-rounded-xl cht-px-5 cht-py-2.5 cht-text-xs cht-font-bold cht-shadow-sm"
                onClick={handleProvisionCourseRepo}
                isLoading={isStarting}
                leftIcon={<GitBranch size={15} />}
              >
                Provision Course Repository
              </Button>
              <CodespaceActionButton
                submissionId={null}
                codespaceUrl={null}
                disabled={true}
                isLoading={isStarting}
                size="sm"
                showHelperNote={false}
              />
            </div>
          ) : (
            <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                isLoading={isRefetching}
                className="cht-rounded-xl cht-text-xs"
                leftIcon={<RefreshCw size={13} className={isRefetching ? "cht-animate-spin" : ""} />}
              >
                Sync Grades
              </Button>
              <CodespaceActionButton
                submissionId={submission?.id}
                submission={submission}
                codespaceUrl={submission?.codespace_url}
                repoFullName={repoName}
                repoUrl={repoUrl}
                disabled={isStarting || (!submission?.codespace_url && !repoUrl && !repoName)}
                isLoading={isStarting}
                size="sm"
                showHelperNote={false}
              />
              <a
                href={repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-bg-slate-900 hover:cht-bg-slate-800 cht-text-white cht-transition-colors"
              >
                <span>Open Course Repo</span>
                <ExternalLink size={13} />
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Provisioned Repository Quick Clone Bar */}
      {submission && repoUrl && (
        <div className="cht-p-3.5 sm:cht-px-4 cht-rounded-2xl cht-bg-slate-900 cht-text-white cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3">
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-min-w-0">
            <Terminal size={15} className="cht-text-emerald-400 cht-shrink-0" />
            <code className="cht-text-xs cht-font-mono cht-text-slate-200 cht-truncate cht-select-all">
              {cloneCommand}
            </code>
          </div>

          <div className="cht-flex items-center cht-gap-3 cht-shrink-0">
            {gradedFilesCount > 0 && (
              <span className="cht-text-[11px] cht-font-bold cht-text-emerald-400 cht-bg-emerald-500/10 cht-border cht-border-emerald-500/20 cht-px-2.5 cht-py-1 cht-rounded-lg">
                {gradedFilesCount} {gradedFilesCount === 1 ? "exercise file" : "exercise files"} evaluated
              </span>
            )}
            <button
              type="button"
              onClick={handleCopyClone}
              className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-rounded-lg cht-bg-slate-800 hover:cht-bg-slate-700 cht-text-xs cht-font-semibold cht-text-slate-200 cht-transition-colors"
            >
              {copiedClone ? (
                <>
                  <Check size={13} className="cht-text-emerald-400" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy size={13} />
                  <span>Copy Clone URL</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * 2. PER-EXERCISE GITHUB AUTOGRADER PANEL (Rendered inside an Exercise Card)
 * ============================================================================ */
interface GitHubAutograderPanelProps {
  exercise: {
    id: number | string;
    title: string;
    exercise_task?: string;
    requires_autograder?: boolean;
    autograder_template_repo?: string;
    autograder_module_name?: string;
    courseTemplateRepo?: string;
    submission?: any;
    exercise_deadline?: string;
  };
  courseId?: number | string;
  moduleId?: number | string;
  track?: string;
  courseTemplateRepo?: string;
}

const GitHubAutograderPanel: React.FC<GitHubAutograderPanelProps> = ({
  exercise,
  courseId,
  moduleId,
  track,
  courseTemplateRepo,
}) => {
  const { addToast } = useToast();

  const [copiedClone, setCopiedClone] = useState(false);
  const [copiedFile, setCopiedFile] = useState(false);

  const targetFile = (
    exercise.autograder_module_name ||
    exercise.submission?.module_name ||
    ""
  ).trim();

  // TanStack Query for course/exercise submission with polling capability
  const {
    data: liveSubmission,
    isFetching: isRefetching,
    refetch,
  } = useExerciseSubmissionQuery(exercise.id, {
    courseId,
    moduleName: targetFile,
  });

  const { mutateAsync: startAssignment, isPending: isStarting } = useStartAssignmentMutation();

  // Combine initial embedded submission with live query data
  const submission = liveSubmission || exercise.submission || null;
  const currentGithubUsername = useResolvedGithubUsername(submission?.github_username);

  const templateRepo =
    courseTemplateRepo ||
    exercise.courseTemplateRepo ||
    exercise.autograder_template_repo ||
    "";

  const repoName = submission?.repository_name;
  const repoUrl = submission?.repository_url;

  // Build deep link to the file or directory in the student's repository
  const isFileTarget = Boolean(targetFile && targetFile.includes("."));
  const targetGitHubUrl =
    repoUrl && targetFile
      ? `${repoUrl.replace(/\/$/, "")}/${isFileTarget ? "blob" : "tree"}/main/${targetFile.replace(/^\/+/, "")}`
      : null;

  const cloneCommand = repoUrl ? `git clone ${repoUrl}.git` : "";

  // Resolve this specific exercise's score from submission.module_scores (or backend module_score)
  const { score: matchedFileScore, matchedKey } = resolveExerciseFileScore(
    submission?.module_scores,
    targetFile
  );

  const rawExerciseScore = (() => {
    if (!submission?.latest_commit_sha) return null;
    if (matchedFileScore !== null) return matchedFileScore;
    if (
      submission?.module_score !== undefined &&
      submission?.module_score !== null &&
      (!targetFile || submission?.module_name === targetFile)
    ) {
      return Number(submission.module_score);
    }
    // Only fall back to overall repo score if NO target file was specified on this exercise
    if (!targetFile && submission?.score !== undefined && submission?.score !== null) {
      return Number(submission.score);
    }
    return null;
  })();

  const hasExerciseScore = rawExerciseScore !== null && !Number.isNaN(rawExerciseScore);
  const exerciseScorePercent = hasExerciseScore ? Math.round(rawExerciseScore!) : 0;
  const isExercisePassed = hasExerciseScore && exerciseScorePercent >= 70;

  const hasOverallCommit = Boolean(submission?.latest_commit_sha);
  const overallScorePercent =
    hasOverallCommit && submission?.score !== undefined && submission?.score !== null
      ? Math.round(Number(submission.score))
      : 0;

  const handleCopyClone = () => {
    if (!cloneCommand) return;
    navigator.clipboard.writeText(cloneCommand);
    setCopiedClone(true);
    addToast("Clone command copied to clipboard!", "success");
    setTimeout(() => setCopiedClone(false), 2500);
  };

  const handleCopyFileName = () => {
    if (!targetFile) return;
    navigator.clipboard.writeText(targetFile);
    setCopiedFile(true);
    addToast(`File path "${targetFile}" copied!`, "success");
    setTimeout(() => setCopiedFile(false), 2000);
  };

  const handleStartAssignment = async () => {
    if (!currentGithubUsername) {
      addToast("Please connect your GitHub account in Profile Settings first.", "warning");
      return;
    }

    try {
      const res = await startAssignment({
        ...(templateRepo ? { template_repo_name: templateRepo } : {}),
        github_username: currentGithubUsername,
        course_id: courseId,
        module_id: moduleId,
        exercise_id: exercise.id,
        track: track,
      });

      addToast(
        res.is_new
          ? "Course repository provisioned on GitHub! You have been invited with push permissions."
          : "Course repository linked! Submit your solution file inside your course repo.",
        "success"
      );
      refetch();
    } catch (err: any) {
      console.error("Failed to start assignment:", err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Failed to provision course repository.";
      addToast(detail, "error");
    }
  };

  return (
    <div className="cht-p-5 sm:cht-p-6 cht-bg-white cht-rounded-[22px] cht-border cht-border-gray-100 cht-shadow-sm cht-space-y-5">
      {/* Header Banner */}
      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3 cht-pb-4 cht-border-b cht-border-gray-100">
        <div className="cht-flex cht-items-center cht-gap-3">
          <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
            <GitBranch size={20} />
          </div>
          <div>
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
              <h4 className="cht-text-sm cht-font-black cht-text-slate-900 !cht-font-heading">
                Exercise Autograder
              </h4>
              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-indigo-50 cht-text-[#3e57da] cht-border cht-border-[#3e57da]/20">
                <Sparkles size={10} /> Per-Exercise Grading
              </span>
            </div>
            <p className="cht-text-xs cht-text-slate-500 font-medium">
              {targetFile
                ? `This exercise is graded automatically by testing "${targetFile}" inside your course GitHub repository.`
                : "Push your solution to your course GitHub repository to run automated grading tests."}
            </p>
          </div>
        </div>

        {exercise.exercise_deadline && (
          <div className="cht-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-bg-amber-50 cht-border cht-border-amber-200/60 cht-text-amber-800 cht-text-xs cht-font-semibold cht-self-start sm:cht-self-auto">
            <Clock size={12} className="cht-text-amber-600" />
            <span>Due: {exercise.exercise_deadline}</span>
          </div>
        )}
      </div>

      {/* Required Exercise Target File Banner */}
      {targetFile && (
        <div className="cht-p-4 cht-rounded-2xl cht-bg-indigo-50/60 cht-border cht-border-[#3e57da]/20 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3">
          <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
            <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-[#3e57da] cht-text-white cht-flex cht-items-center cht-justify-center cht-shrink-0">
              <FileCode2 size={18} />
            </div>
            <div className="cht-min-w-0">
              <span className="cht-block cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider cht-text-[#3e57da]">
                Expected Exercise File in Course Repository
              </span>
              <div className="cht-flex cht-items-center cht-gap-2 cht-mt-0.5">
                <code className="cht-text-xs sm:cht-text-sm cht-font-mono cht-font-bold cht-text-slate-900 cht-bg-white cht-px-2.5 cht-py-0.5 cht-rounded-lg cht-border cht-border-indigo-200/80 cht-truncate">
                  {targetFile}
                </code>
                <button
                  type="button"
                  onClick={handleCopyFileName}
                  className="cht-p-1.5 cht-rounded-lg cht-bg-white hover:cht-bg-indigo-100 cht-text-slate-600 cht-border cht-border-indigo-200/60 cht-transition-colors"
                  title="Copy expected file path"
                >
                  {copiedFile ? <Check size={13} className="cht-text-emerald-600" /> : <Copy size={13} />}
                </button>
              </div>
            </div>
          </div>

          {targetGitHubUrl && (
            <a
              href={targetGitHubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-bg-white hover:cht-bg-[#3e57da] cht-text-[#3e57da] hover:cht-text-white cht-border cht-border-[#3e57da]/25 cht-transition-all cht-shrink-0 cht-shadow-sm"
            >
              <FileCode2 size={14} />
              <span>Open {targetFile} on GitHub</span>
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}

      {/* GitHub Account Link Status */}
      {!currentGithubUsername ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="cht-p-5 cht-rounded-[20px] cht-bg-amber-50/80 cht-border cht-border-amber-200/80 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-4"
        >
          <div className="cht-flex cht-items-start cht-gap-3.5">
            <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-amber-100 cht-text-amber-700 cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-mt-0.5">
              <AlertTriangle size={18} />
            </div>
            <div>
              <h5 className="cht-text-xs cht-font-bold cht-text-amber-950">
                GitHub Account Required for Autograding
              </h5>
              <p className="cht-text-[11px] cht-text-amber-800 leading-relaxed cht-mt-0.5">
                Connect your GitHub username in your Profile Settings so our system can provision your private course repository and grade this exercise.
              </p>
            </div>
          </div>

          <Link
            to="/student/dashboard/profile?tab=edit-profile"
            className="cht-inline-flex cht-items-center cht-justify-center cht-gap-2 cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-xs cht-font-bold cht-bg-[#1e293b] hover:cht-bg-slate-900 cht-text-white cht-transition-all cht-shrink-0 cht-shadow-sm"
          >
            <span>Connect in Profile Settings</span>
            <ArrowRight size={13} />
          </Link>
        </motion.div>
      ) : (
        <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-p-3.5 cht-rounded-[16px] cht-bg-slate-50 cht-border cht-border-slate-100 cht-gap-2.5">
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-slate-900 cht-text-white cht-flex cht-items-center cht-justify-center cht-text-xs cht-font-bold cht-shrink-0">
              <GitBranch size={14} />
            </div>
            <span className="cht-text-xs cht-text-slate-600 font-medium">
              Connected GitHub handle:{" "}
              <a
                href={`https://github.com/${currentGithubUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cht-font-bold cht-text-slate-900 hover:cht-text-[#3e57da] cht-inline-flex cht-items-center cht-gap-1"
              >
                @{currentGithubUsername}
                <ExternalLink size={11} className="cht-text-slate-400" />
              </a>
            </span>
          </div>

          <div className="cht-flex cht-items-center cht-gap-3">
            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-font-bold cht-text-emerald-600">
              <CheckCircle size={13} /> Ready
            </span>
            <Link
              to="/student/dashboard/profile?tab=edit-profile"
              className="cht-text-[11px] cht-font-semibold cht-text-slate-400 hover:cht-text-[#3e57da] cht-transition-colors"
            >
              Change in Settings
            </Link>
          </div>
        </div>
      )}

      {/* Main Action / Repository Workspace State */}
      {!submission ? (
        /* State 1: Course Repository Not Provisioned Yet */
        <div className="cht-p-6 cht-rounded-[20px] cht-bg-[#f2f4fd]/50 cht-border cht-border-[#3e57da]/15 cht-text-center cht-space-y-4">
          <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-mx-auto">
            <Code2 size={24} />
          </div>
          <div className="cht-max-w-md cht-mx-auto cht-space-y-1.5">
            <h5 className="cht-text-sm cht-font-bold cht-text-slate-900">
              Provision Your Course GitHub Repository
            </h5>
            <p className="cht-text-xs cht-text-slate-500 leading-relaxed">
              Provision your course repository once to work on this exercise
              {targetFile ? (
                <>
                  {" "}
                  (<code className="cht-font-mono cht-font-bold cht-text-slate-800">{targetFile}</code>)
                </>
              ) : null}{" "}
              and all other autograded exercises in this course.
              {templateRepo && (
                <>
                  <br />
                  <span className="cht-text-[11px] cht-text-slate-400">Course Template: </span>
                  <code className="cht-text-[#3e57da] cht-font-bold cht-bg-white cht-px-2 cht-py-0.5 cht-rounded-md cht-border cht-border-slate-200/80 cht-mt-1 cht-inline-block">
                    {templateRepo}
                  </code>
                </>
              )}
            </p>
          </div>

          <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-justify-center cht-gap-3">
            <Button
              variant="primary"
              size="md"
              className="cht-rounded-xl cht-px-6 cht-shadow-md"
              onClick={handleStartAssignment}
              isLoading={isStarting}
              disabled={!currentGithubUsername}
              leftIcon={<GitBranch size={16} />}
            >
              Provision Course Repository on GitHub
            </Button>

            <CodespaceActionButton
              submissionId={null}
              codespaceUrl={null}
              disabled={true}
              isLoading={isStarting}
              showHelperNote={false}
              size="md"
            />
          </div>
          <p className="cht-text-[11px] cht-text-slate-400">
            Once your course repository is provisioned, click{" "}
            <span className="cht-font-semibold cht-text-slate-600">Launch in Codespaces</span> to open a zero-setup VS Code environment directly in your browser.
          </p>
        </div>
      ) : (
        /* State 2 & 3: Provisioned Course Repository & Per-Exercise Scorecard */
        <div className="cht-space-y-5">
          {/* Active Course Repository Card */}
          <div className="cht-p-4 sm:cht-p-5 cht-rounded-[18px] cht-bg-slate-900 cht-text-white cht-space-y-3.5 cht-shadow-md">
            <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-2.5">
              <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                <Terminal size={16} className="cht-text-emerald-400" />
                <span className="cht-text-xs cht-font-mono cht-text-slate-300">
                  {repoName}
                </span>
                <span className="cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-slate-800 cht-text-slate-300 cht-border cht-border-slate-700">
                  Course Repo
                </span>
              </div>

              <div className="cht-flex cht-items-center cht-gap-2.5 cht-flex-wrap">
                {targetGitHubUrl && (
                  <a
                    href={targetGitHubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-bold cht-text-indigo-300 hover:cht-text-indigo-200 cht-bg-indigo-500/15 cht-border cht-border-indigo-400/30 cht-px-2.5 cht-py-1.5 cht-rounded-lg cht-transition-colors"
                  >
                    <FileCode2 size={13} />
                    <span>{targetFile}</span>
                    <ExternalLink size={11} />
                  </a>
                )}
                <a
                  href={repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-rounded-lg cht-bg-[#21262d] hover:cht-bg-[#30363d] cht-border cht-border-[#30363d] cht-text-xs cht-font-bold cht-text-slate-200 hover:cht-text-white cht-transition-colors"
                >
                  <span>Open Course Repo</span>
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>

            {/* Zero-Setup Cloud IDE Launch Bar (GitHub Codespaces) */}
            <div className="cht-p-3.5 cht-rounded-xl cht-bg-[#161b22] cht-border cht-border-[#30363d] cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3">
              <CodespaceActionButton
                submissionId={submission?.id}
                submission={submission}
                codespaceUrl={submission?.codespace_url}
                repoFullName={repoName}
                repoUrl={repoUrl}
                disabled={isStarting || (!submission?.codespace_url && !repoUrl && !repoName)}
                isLoading={isStarting}
                showHelperNote={true}
                size="md"
              />
            </div>

            {/* Quick Clone Terminal Box */}
            <div className="cht-flex cht-items-center cht-justify-between cht-bg-slate-950/80 cht-border cht-border-slate-800 cht-p-2.5 cht-rounded-xl cht-font-mono cht-text-xs cht-text-slate-300">
              <span className="cht-truncate cht-select-all cht-mr-2">
                {cloneCommand}
              </span>
              <button
                type="button"
                onClick={handleCopyClone}
                className="cht-p-1.5 cht-rounded-lg cht-bg-slate-800 hover:cht-bg-slate-700 cht-text-slate-300 cht-transition-colors cht-shrink-0"
                title="Copy git clone command"
              >
                {copiedClone ? <Check size={14} className="cht-text-emerald-400" /> : <Copy size={14} />}
              </button>
            </div>

            <p className="cht-text-[11px] cht-text-slate-400 leading-relaxed">
              {targetFile ? (
                <>
                  Open your repository in{" "}
                  <span className="cht-text-emerald-400 cht-font-semibold">GitHub Codespaces</span> or clone it locally, implement your solution in{" "}
                  <code className="cht-text-indigo-300 cht-font-bold">{targetFile}</code>, then commit and push to{" "}
                  <code className="cht-text-emerald-300">main</code>.
                </>
              ) : (
                <>
                  Open your repository in{" "}
                  <span className="cht-text-emerald-400 cht-font-semibold">GitHub Codespaces</span> or clone it locally, implement your solution, and push to{" "}
                  <code className="cht-text-emerald-300">main</code>.
                </>
              )}
            </p>
          </div>

          {/* Per-Exercise Autograder Scorecard */}
          <div className="cht-p-5 cht-rounded-[20px] cht-bg-slate-50 cht-border cht-border-slate-100 cht-space-y-4">
            <div className="cht-flex cht-items-center cht-justify-between">
              <div className="cht-flex cht-items-center cht-gap-2">
                <ShieldCheck size={18} className="cht-text-[#3e57da]" />
                <h5 className="cht-text-xs cht-font-bold cht-text-slate-800 cht-uppercase cht-tracking-wider">
                  {targetFile ? `Exercise Grade (${targetFile})` : "Exercise Grade"}
                </h5>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                isLoading={isRefetching}
                className="cht-h-8 cht-px-2.5 cht-text-xs cht-text-slate-600 hover:cht-text-[#3e57da]"
                leftIcon={<RefreshCw size={13} className={isRefetching ? "cht-animate-spin" : ""} />}
              >
                Sync Grade
              </Button>
            </div>

            {hasExerciseScore ? (
              <div className="cht-space-y-4">
                {/* Per-Exercise Score Header */}
                <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3 cht-p-4 cht-rounded-2xl cht-bg-white cht-border cht-border-gray-100">
                  <div className="cht-space-y-1">
                    <span className="cht-text-[11px] cht-font-bold cht-text-slate-400 cht-uppercase cht-tracking-widest">
                      {targetFile ? `Score for ${targetFile}` : "Exercise Score"}
                    </span>
                    <div className="cht-flex cht-items-baseline cht-gap-2 cht-flex-wrap">
                      <span
                        className={`cht-text-3xl cht-font-black !cht-font-heading ${
                          isExercisePassed ? "cht-text-emerald-600" : "cht-text-amber-600"
                        }`}
                      >
                        {exerciseScorePercent}%
                      </span>
                      {matchedKey && (
                        <span className="cht-text-xs cht-text-slate-500 font-mono">
                          ({matchedKey})
                        </span>
                      )}
                    </div>
                    {hasOverallCommit && (
                      <p className="cht-text-[11px] cht-text-slate-400 font-medium">
                        Overall Course Repo Score:{" "}
                        <span className="cht-font-bold cht-text-slate-700">{overallScorePercent}%</span>{" "}
                        ({submission.passed_tests || 0}/{submission.total_tests || 0} total repo tests)
                      </p>
                    )}
                  </div>

                  <div>
                    {isExercisePassed ? (
                      <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1.5 cht-rounded-full cht-text-xs cht-font-bold cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200">
                        <CheckCircle2 size={15} /> Exercise Passed (70% Required)
                      </span>
                    ) : (
                      <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1.5 cht-rounded-full cht-text-xs cht-font-bold cht-bg-amber-50 cht-text-amber-800 cht-border cht-border-amber-200">
                        <AlertTriangle size={15} /> Needs Revision (&lt;70%)
                      </span>
                    )}
                  </div>
                </div>

                {/* Exercise Score Progress Bar */}
                <div className="cht-w-full cht-h-2.5 cht-bg-slate-200 cht-rounded-full cht-overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(exerciseScorePercent, 100)}%` }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className={`cht-h-full ${
                      isExercisePassed ? "cht-bg-emerald-500" : "cht-bg-amber-500"
                    }`}
                  />
                </div>
              </div>
            ) : (
              /* Provisioned, awaiting test result for this specific exercise file */
              <div className="cht-p-6 cht-rounded-2xl cht-bg-white cht-border cht-border-gray-100 cht-text-center cht-space-y-2">
                <Clock size={20} className="cht-text-slate-400 cht-mx-auto cht-animate-pulse" />
                <h6 className="cht-text-xs cht-font-bold cht-text-slate-800">
                  {targetFile
                    ? `Awaiting Autograder Result for "${targetFile}"`
                    : "Awaiting Your First Solution Push"}
                </h6>
                <p className="cht-text-[11px] cht-text-slate-500 max-w-sm cht-mx-auto leading-relaxed">
                  {targetFile
                    ? `Ensure "${targetFile}" is created/updated in your course repository, commit your changes, and push to main. Only the test suite for "${targetFile}" grades this exercise.`
                    : "Clone your course repository, commit your code, and push to main. The autograder will evaluate your tests automatically and update this screen."}
                </p>
              </div>
            )}

            {/* All Graded Files in Course Repo Breakdown */}
            {submission.module_scores && Object.keys(submission.module_scores).length > 0 && (
              <div className="cht-space-y-2 cht-pt-2">
                <div className="cht-flex cht-items-center cht-gap-1.5">
                  <Layers size={13} className="cht-text-slate-400" />
                  <p className="cht-text-[11px] cht-font-bold cht-text-slate-500 cht-uppercase cht-tracking-wider">
                    All Graded Exercise Files in Course Repo
                  </p>
                </div>
                <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-2">
                  {Object.entries(submission.module_scores).map(([fileKey, fileScore]: [string, any]) => {
                    const isCurrentExercise = Boolean(matchedKey && fileKey === matchedKey);
                    const numericScore = Math.round(Number(fileScore) || 0);
                    return (
                      <div
                        key={fileKey}
                        className={`cht-p-3 cht-rounded-xl cht-border cht-flex cht-items-center cht-justify-between ${
                          isCurrentExercise
                            ? "cht-bg-indigo-50/70 cht-border-[#3e57da]/40 cht-ring-1 cht-ring-[#3e57da]/20"
                            : "cht-bg-white cht-border-gray-100"
                        }`}
                      >
                        <div className="cht-flex cht-items-center cht-gap-2 cht-min-w-0">
                          <FileCode2
                            size={13}
                            className={isCurrentExercise ? "cht-text-[#3e57da] cht-shrink-0" : "cht-text-slate-400 cht-shrink-0"}
                          />
                          <span className="cht-text-xs cht-font-mono cht-font-medium cht-text-slate-700 cht-truncate">
                            {fileKey}
                          </span>
                          {isCurrentExercise && (
                            <span className="cht-px-1.5 cht-py-0.5 cht-rounded cht-text-[9px] cht-font-bold cht-bg-[#3e57da] cht-text-white cht-shrink-0">
                              This Exercise
                            </span>
                          )}
                        </div>
                        <span
                          className={`cht-text-xs cht-font-bold ${
                            numericScore >= 70 ? "cht-text-emerald-600" : "cht-text-amber-600"
                          }`}
                        >
                          {numericScore}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Commit SHA Footnote */}
            {submission.latest_commit_sha && (
              <p className="cht-text-[11px] cht-text-slate-400 font-mono cht-pt-1">
                Latest evaluated commit SHA:{" "}
                <span className="cht-text-slate-600 cht-font-bold">
                  #{submission.latest_commit_sha.slice(0, 7)}
                </span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubAutograderPanel;
